import type { HomeyAPI, App } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type AppProps = {
  [K in keyof App as App[K] extends Function ? never : K]: App[K];
};

type AppsData = { [key: string]: App };
type AppsById = { [key: string]: AppProps };

interface State {
  data: AppsData | null;
  byId: AppsById | null;
  isLoading: boolean;
  error: unknown;
}

export type AppsStoreState = State;

export class AppsStore extends BaseApiStore<State, App> {
  constructor() {
    super('apps');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.apps, reference: this });

      if (abortController.signal.aborted) return;

      const appsPromise = api.apps.getApps({ $cache: cache });
      const [appsData] = await Promise.all([appsPromise]);

      if (abortController.signal.aborted) return;

      const apps = Object.values(appsData);

      const data: AppsData = {};
      const byId: AppsById = {};

      for (const appInstance of apps) {
        const appReference = { ...appInstance };

        data[appReference.id] = appInstance;
        byId[appReference.id] = appReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.apps, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.apps.on('app.create', this.handleCreate);
    api.apps.on('app.update', this.handleUpdate);
    api.apps.on('app.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.apps.off('app.create', this.handleCreate);
    api.apps.off('app.update', this.handleUpdate);
    api.apps.off('app.delete', this.handleDelete);
  }

  getInstanceFromCache(app: { id: string }) {
    return this.getCache()[app.id];
  }

  getCache(): { [key: string]: App } {
    const api = this.getApi();
    const cache = api.apps.__cache['app'];
    return cache;
  }

  handleCreate = (createdApp: App) => {
    this.DEBUG && console.info(`create:app:${createdApp.id}`);
    const state = this.get();
    const appInstance = this.getInstanceFromCache(createdApp);

    if (appInstance == null) return;

    const appReference = { ...appInstance };

    this.set({
      data: {
        ...state.data,
        [createdApp.id]: appInstance,
      },
      byId: {
        ...state.byId,
        [createdApp.id]: appReference,
      },
    });
  };

  handleUpdate = (updatedApp: App) => {
    this.DEBUG && console.info(`update:app:${updatedApp.id}`);

    this.addToQueue(updatedApp.id, updatedApp);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedApp] of Object.entries(updateQueue)) {
        const appInstance = this.getInstanceFromCache(queuedApp);

        if (state.data == null) {
          this.DEBUG && console.info(`update:app:missing-data`);
          continue;
        }

        if (appInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:app:unknown:${id}`);
          continue;
        }

        const appReference = { ...appInstance };

        nextById[id] = appReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:app:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedApp: App) => {
    this.DEBUG && console.info(`delete:app:${deletedApp.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:app:missing-data`);
      return;
    }

    const appInstance = state.data[deletedApp.id];

    if (appInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[appInstance.id];
    delete nextById[appInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const apps = Object.values(cache ?? {});
    const data: AppsData = {};
    const byId: AppsById = {};

    for (const appInstance of apps) {
      const appReference = { ...appInstance };
      data[appReference.id] = appInstance;
      byId[appReference.id] = appReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const appsStore = new AppsStore();
